import { denormalize } from 'normalizr';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { constants, utils } from '@frontend/partviewer';

import { partLibraryViewerPage } from 'modules/viewerPage/data/actions';
import { getActiveFileUUID } from 'modules/viewerPage/data/reducer';

import type { ActionOfType } from 'store/types';

import {
  types,
  cadViewer,
  featureSnapTo,
  viewerInteractions,
  type CadViewerAction,
  type ViewerBodyIdsPayload,
} from '../actions';
import { viewerBodySchema } from '../partFileCadData/entities';
import {
  getAllViewerBodiesHash,
  getAllViewerParts,
  getCadData,
  getPartFileCadDataForViewerBody,
  getViewerIdsForPartFileUuid,
} from '../partFileCadData/reducer';
import {
  getActiveGeometricPartFileUuids,
  getFocusedBodies,
  getPrevFocusedBodies,
  getVisibleBodies,
} from '../reducer';

const { FORCE_VIEW_TYPES } = constants;
const { areArraysEqual } = utils;

export default function* root() {
  yield takeLatest(types.RESET_VIEW, watchResetView);
  yield takeLatest(types.FEATURE_SNAP_TO_REQUEST, watchFeatureSnapTo);
  yield takeLatest(
    types.VIEWER_INTERACTIONS_FOCUS_ON_BODIES,
    watchFocusOnBodies,
  );
  yield takeLatest(
    types.VIEWER_INTERACTIONS_FOCUS_ON_PART_FILE,
    watchFocusOnPartFile,
  );
  yield takeLatest(
    types.VIEWER_INTERACTIONS_HIDE_VIEWER_BODY,
    watchHideViewerBody,
  );
  yield takeLatest(
    types.VIEWER_INTERACTIONS_ISOLATE_BODIES,
    watchIsolateOnBodies,
  );
  yield takeLatest(
    types.VIEWER_INTERACTIONS_SHOW_ALL_COMPONENTS,
    watchShowAllComponents,
  );
}

const ancestorsAndSelf = function* (viewerBodyId, viewerBodyHash) {
  // Finds all ancestor viewer body ids, including the original viewer body id
  if (viewerBodyHash) {
    const stack = [viewerBodyId];
    const _ancestorsAndSelf = [];
    let currentBodyId;
    //eslint-disable-next-line no-cond-assign
    while ((currentBodyId = stack.pop())) {
      const parentViewerId = viewerBodyHash[currentBodyId];
      if (parentViewerId) {
        stack.push(parentViewerId);
      }
      _ancestorsAndSelf.unshift(currentBodyId);
    }
    yield* _ancestorsAndSelf;
  }
};

const selfAndDescendantBodies = function* (denormalizedViewerBody) {
  const stack = [denormalizedViewerBody];
  let currentBody;
  //eslint-disable-next-line no-cond-assign
  while ((currentBody = stack.pop())) {
    if (currentBody.children?.length) {
      for (const childBody of currentBody.children) {
        stack.push(childBody);
      }
    } else if (currentBody.viewerId) {
      yield currentBody.viewerId;
    }
  }
};

function* focusSceneOnBodies({ viewerBodyIds }: ViewerBodyIdsPayload) {
  /*
  Replaces the scene to show only the desired bodies passed in as a prop.

  activeBodies will be set to the desired viewer bodies
  expandedBodies will be set to all assembly parents of the desired body and the desired body if it is an assembly
  enabledBodies will be set to the desired body and all of its descendents.
  visibleBodies will be set to all non-assembly descenedents of the desired body, including the desired body if it is not an assembly.
  */
  try {
    const activeBodies = [...viewerBodyIds];
    const enabledBodies = [];
    const expandedBodies = [];
    const visibleBodies = [];

    const partFileCadDataState: ReturnType<typeof getCadData> = yield select(
      getCadData,
    );

    for (const viewerBodyId of viewerBodyIds) {
      for (const { entities } of Object.values(partFileCadDataState)) {
        // find the partfile that this body belongs to
        if (entities?.viewerBodies?.[viewerBodyId]) {
          const denormalizedViewerBody = denormalize(
            viewerBodyId,
            viewerBodySchema,
            entities,
          );
          enabledBodies.push(
            viewerBodyId,
            ...denormalizedViewerBody.descendentBodyViewerIds,
          );
          expandedBodies.push(
            ...ancestorsAndSelf(viewerBodyId, entities?.viewerBodies),
          );
          visibleBodies.push(
            ...selfAndDescendantBodies(denormalizedViewerBody),
          );
        }
      }
    }

    yield all([
      put(cadViewer.receiveActiveBodies({ activeBodies })),
      put(cadViewer.receiveExpandedBodies({ expandedBodies })),
      put(cadViewer.receiveVisibleBodies({ visibleBodies })),
      put(cadViewer.receiveEnabledBodies({ enabledBodies })),
    ]);
  } catch (error) {
    console.error('Error in focus scene on bodies', error);
    throw error;
  }
}

function* watchFeatureSnapTo(
  action: ActionOfType<CadViewerAction, typeof types.FEATURE_SNAP_TO_REQUEST>,
) {
  try {
    const { feature } = action.payload;
    if (feature) {
      const { referenceUuid } = feature;
      const viewerIds: ReturnType<typeof getViewerIdsForPartFileUuid> =
        yield select(getViewerIdsForPartFileUuid, referenceUuid);

      const visibleBodies: ReturnType<typeof getVisibleBodies> = yield select(
        getVisibleBodies,
      );

      let isVisible = false;
      for (const id of viewerIds) {
        if (visibleBodies.includes(id)) {
          isVisible = true;
          break;
        }
      }

      if (!isVisible) {
        if (viewerIds.length === 1) {
          yield put(
            cadViewer.receiveVisibleBodies({
              visibleBodies: visibleBodies.concat([viewerIds[0]]),
            }),
          );
        } else if (viewerIds.length > 1) {
          // defer to presence in focused bodies
          const focusedBodies: ReturnType<typeof getFocusedBodies> =
            yield select(getFocusedBodies);
          for (const id of viewerIds) {
            if (focusedBodies.includes(id)) {
              yield put(
                cadViewer.receiveVisibleBodies({
                  visibleBodies: visibleBodies.concat([id]),
                }),
              );
              break;
            }
          }
        }
      }
    }

    yield put(featureSnapTo.receive({ feature }));
  } catch (error) {
    console.error('Error in watchFeatureSnapTo', error);
  }
}

function* watchFocusOnPartFile(
  action: ActionOfType<
    CadViewerAction,
    typeof types.VIEWER_INTERACTIONS_FOCUS_ON_PART_FILE
  >,
) {
  /*
  Finds a body related to a partfile and sets it to focus.
  */
  try {
    const { partFileUuid } = action.payload;
    // 1. Find a viewer part related to the part file
    const allViewerParts: ReturnType<typeof getAllViewerParts> = yield select(
      getAllViewerParts,
    );
    const viewerPart = allViewerParts.find(
      ({ partFile }) => partFile === partFileUuid,
    );
    if (!viewerPart) {
      return;
    }
    const viewerPartId = viewerPart.id;

    // 2. Find the viewer Body
    const allViewerBodies: ReturnType<typeof getAllViewerBodiesHash> =
      yield select(getAllViewerBodiesHash);
    const viewerBody = Object.values(allViewerBodies).find(
      ({ viewerPart }) => viewerPart === viewerPartId,
    );

    yield put(
      viewerInteractions.focusOnBodies({
        viewerBodyIds: [viewerBody.viewerId],
      }),
    );
  } catch (error) {
    console.error('Error in watchFocusOnPartFile', error);
  }
}

function* watchFocusOnBodies(
  action: ActionOfType<
    CadViewerAction,
    typeof types.VIEWER_INTERACTIONS_FOCUS_ON_BODIES
  >,
) {
  try {
    const { viewerBodyIds } = action.payload;
    const oldFocusedBodies: ReturnType<typeof getPrevFocusedBodies> =
      yield select(getPrevFocusedBodies);
    yield put(cadViewer.receiveFocusedBodies({ focusedBodies: viewerBodyIds }));
    yield call(focusSceneOnBodies, { viewerBodyIds });

    // remove selections
    yield put(cadViewer.receiveSelectedBodies({ selectedBodies: [] }));

    if (
      !oldFocusedBodies.length ||
      areArraysEqual(viewerBodyIds, oldFocusedBodies)
    ) {
      // need to force the update in the renderer
      yield put(cadViewer.receiveForceFocus({ forceFocus: true }));
    }

    const firstViewerId = viewerBodyIds[0];
    if (!firstViewerId) {
      return;
    }

    const partFileCadData: ReturnType<typeof getPartFileCadDataForViewerBody> =
      yield select(getPartFileCadDataForViewerBody, firstViewerId);

    const viewerBody = partFileCadData.entities.viewerBodies[firstViewerId];

    const viewerPart =
      partFileCadData.entities.viewerParts[viewerBody.viewerPart];
    yield put(
      cadViewer.receiveFocusedUuid({ focusedUuid: viewerPart.partFile }),
    );

    const activeFocusedPartFileUuid: ReturnType<typeof getActiveFileUUID> =
      yield select(getActiveFileUUID);
    if (activeFocusedPartFileUuid !== viewerPart.partFile) {
      yield put(
        partLibraryViewerPage.setActiveUUID({
          forceFocus: false,
          partFileUuid: viewerPart.partFile,
        }),
      );
    }
  } catch (error) {
    console.error('Error in watchFocusOnBodies', error);
  }
}

function* watchIsolateOnBodies(
  action: ActionOfType<
    CadViewerAction,
    typeof types.VIEWER_INTERACTIONS_ISOLATE_BODIES
  >,
) {
  /*
  Updates visible bodies to be only the requested bodies and descendents of those bodies.
  */
  try {
    const { viewerBodyIds } = action.payload;
    const allViewerBodies: ReturnType<typeof getAllViewerBodiesHash> =
      yield select(getAllViewerBodiesHash);
    yield put(
      cadViewer.receiveVisibleBodies({
        visibleBodies: viewerBodyIds.flatMap((viewerBodyId) => [
          viewerBodyId,
          ...(allViewerBodies?.[viewerBodyId]?.descendentBodyViewerIds ?? []),
        ]),
      }),
    );
  } catch (error) {
    console.error('Error in watchIsolateOnBodies', error);
  }
}

function* watchResetView() {
  try {
    const focusedBodies: ReturnType<typeof getFocusedBodies> = yield select(
      getFocusedBodies,
    );

    yield call(focusSceneOnBodies, { viewerBodyIds: focusedBodies });
    yield put(
      cadViewer.receiveForceView({
        forceView: { type: FORCE_VIEW_TYPES.RESET_VIEW },
      }),
    );
  } catch (error) {
    console.error('Error in watch reset view', error);
  }
}

function* watchShowAllComponents() {
  /*
  Set all viewer bodies for all active geometric parts to be visible.
  */
  try {
    const activePartFileUuids: ReturnType<
      typeof getActiveGeometricPartFileUuids
    > = yield select(getActiveGeometricPartFileUuids);
    const allPartFileCadData: ReturnType<typeof getCadData> = yield select(
      getCadData,
    );

    let allViewerBodies: string[] = [];
    activePartFileUuids.forEach((partFileUuid) => {
      for (const { entities } of Object.values(allPartFileCadData)) {
        const viewerPart = Object.values(entities?.viewerParts || {}).find(
          ({ partFile }) => partFile === partFileUuid,
        );
        if (viewerPart) {
          const viewerBodyEntities = entities?.viewerBodies || {};
          const viewerBodyIds = Object.keys(viewerBodyEntities);
          allViewerBodies = allViewerBodies.concat(viewerBodyIds);
          break;
        }
      }
    });
    yield put(
      cadViewer.receiveVisibleBodies({
        visibleBodies: allViewerBodies,
      }),
    );
  } catch (error) {
    console.error('Error in watchShowAllComponents', error);
  }
}

function* watchHideViewerBody(
  action: ActionOfType<
    CadViewerAction,
    typeof types.VIEWER_INTERACTIONS_HIDE_VIEWER_BODY
  >,
) {
  /*
  Removes a viewer body, and any of its parents, from being visible.
  */
  try {
    const { viewerBodyId } = action.payload;

    // find all of the viewer bodys to hide
    const partFileCadDataState: ReturnType<typeof getCadData> = yield select(
      getCadData,
    );
    const viewerBodies = Object.values(partFileCadDataState).find(
      ({ entities }) => entities?.viewerBodies?.[viewerBodyId],
    )?.entities?.viewerBodies;
    const excludedViewerBodyIds = new Set(
      ancestorsAndSelf(viewerBodyId, viewerBodies),
    ).add(viewerBodyId); //unlikely, but viewerBodyId is added in case viewerBodies is undefined

    // filter visible bodies to remove hidden bodies
    const visibleBodies: ReturnType<typeof getVisibleBodies> = yield select(
      getVisibleBodies,
    );
    const nextVisibleBodies = visibleBodies.filter(
      (viewerBodyId) => !excludedViewerBodyIds.has(viewerBodyId),
    );
    yield put(
      cadViewer.receiveVisibleBodies({
        visibleBodies: nextVisibleBodies,
      }),
    );
  } catch (error) {
    console.error('There was an error in watchHideViewerBody');
  }
}
